import authService from './authService';
import config from '../config';

const UI_STATE_API_PREFIX = '/api/v1/ui-state';

/**
 * Retrieves the complete UI state object for the current user.
 * @returns {Promise<Object>} The user's settings object.
 */
export const getUiState = async () => {
  const url = `${config.API_BASE_URL}${UI_STATE_API_PREFIX}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch UI state: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[uiStateApi] Error fetching UI state:', error);
    authService.trackNetworkError('getUiState', error);
    throw error;
  }
};

/**
 * Updates or adds settings to the user's UI state.
 * Performs a deep merge on the backend.
 * @param {Object} patch - The object containing keys to update.
 * @returns {Promise<Object>} The full, updated UI state object.
 */
export const updateUiState = async (patch) => {
  const url = `${config.API_BASE_URL}${UI_STATE_API_PREFIX}`;
  try {
    const response = await authService.authFetch(url, {
      method: 'PATCH',
      body: patch,
    });
    if (!response.ok) {
      let errorDetail = `Failed to update UI state: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[uiStateApi] Error updating UI state:', error);
    authService.trackNetworkError('updateUiState', error);
    throw error;
  }
};

/**
 * Retrieves a single value from the UI state by key.
 * @param {string} key - The specific key to retrieve.
 * @returns {Promise<any>} The raw value of the key (primitive or object).
 */
export const getUiStateKey = async (key) => {
  if (!key) throw new Error("Key is required");
  const url = `${config.API_BASE_URL}${UI_STATE_API_PREFIX}/${key}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch UI state key '${key}': ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error(`[uiStateApi] Error fetching key '${key}':`, error);
    authService.trackNetworkError('getUiStateKey', error);
    throw error;
  }
};

/**
 * Sets a single key in the UI state to a specific value.
 * @param {string} key - The key to set.
 * @param {any} value - The value to store (will be JSON stringified).
 * @returns {Promise<Object>} The full, updated UI state object.
 */
export const setUiStateKey = async (key, value) => {
  if (!key) throw new Error("Key is required");
  const url = `${config.API_BASE_URL}${UI_STATE_API_PREFIX}/${key}`;
  try {
    // Note: The body passed to authFetch is usually stringified automatically if it's an object,
    // but authFetch implementation checks `typeof body === 'object'`.
    // Since 'value' might be a primitive (string, boolean, number), we need to handle it carefully
    // to ensure authFetch treats it as JSON content.
    // Assuming standard fetch behavior, we send the raw value stringified.
    const response = await authService.authFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(value), // Directly stringify the value for the payload
    });

    if (!response.ok) {
      let errorDetail = `Failed to set UI state key '${key}': ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error(`[uiStateApi] Error setting key '${key}':`, error);
    authService.trackNetworkError('setUiStateKey', error);
    throw error;
  }
};

export default {
  getUiState,
  updateUiState,
  getUiStateKey,
  setUiStateKey
};